import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/ui/Logo";
import { 
  ArrowLeft, 
  Lock, 
  Mail, 
  ShieldCheck, 
  Database, 
  Server, 
  Loader2,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (user && !isLoading) {
      navigate("/");
    }
  }, [user, isLoading, navigate]);

  const handleGoogleLogin = async () => {
    setError("");
    setIsGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || "Erreur de connexion Google");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || "Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-white flex flex-col lg:flex-row overflow-hidden selection:bg-blue-100 selection:text-blue-900 font-sans">
      {/* Styles pour les animations locales */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fade-up 0.8s ease-out forwards;
        }
      ` }} />

      {/* Left Column - Image & Branding (Desktop Only) */}
      <section 
        className="relative hidden lg:block lg:w-1/2 bg-slate-950 overflow-hidden"
      >
        {/* Pattern Overlay - Optimized for WebP */}
        {/* LCP Optimized Image - Hidden from mobile to save bandwidth */}
        <picture className="absolute inset-0 w-full h-full">
          <source media="(min-width: 1024px)" srcSet="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&fm=webp" />
          <img 
            alt=""
            className="w-full h-full object-cover opacity-40 mix-blend-overlay grayscale"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
        </picture>
        
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-slate-950/50" aria-hidden="true" />

        <div className="relative h-full flex flex-col justify-between p-12 lg:pl-28 lg:pr-16 z-10">
          <div className="flex items-center gap-2 group cursor-pointer">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20 group-hover:scale-110 transition-all duration-500">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <span className="text-lg font-black tracking-tighter text-white uppercase group-hover:translate-x-1 transition-all">Domotus App & ERP</span>
          </div>

          <div className="space-y-8">
            <div
              className="animate-fade-up opacity-0"
              style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
            >
              <span className="inline-block px-4 py-1.5 bg-blue-600/10 border border-blue-600/20 rounded-full text-[10px] font-black text-blue-400 uppercase tracking-[0.3em] mb-4">
                <Database className="w-4 h-4 inline-block mr-2" /> Actif Immobilier Digital
              </span>
              <h2 className="text-4xl xl:text-5xl font-black tracking-tighter text-white mb-6 leading-tight">
                L'intégralité de votre<br/>patrimoine domotique<br/>
                <span className="text-blue-500 italic lowercase">dans votre poche.</span>
              </h2>
            </div>
            
            <p className="text-slate-400 font-medium text-lg leading-relaxed max-w-xl">
              L'Espace Client Domotus n'est pas qu'un portail SAV. C'est le Jumeau Numérique exclusif de votre villa ou bâtiment tertiaire, garantissant sa pérennité et sa valorisation à la revente.
            </p>

            {/* Bullet Points Section */}
            <ul 
              className="space-y-5 pt-4 animate-fade-up opacity-0"
              style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}
            >
              {[
                "Accès Jumeau Numérique 3D",
                "Suivi de Chantier en Temps Réel",
                "Historique Complet des Interventions",
                "Pilotage & Supervision via ERP Intégré"
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-4 group">
                  <div className="w-7 h-7 rounded-full bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/30 transition-colors">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-300 text-base font-semibold tracking-wide">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-6 text-slate-500">
            <div className="flex flex-col">
              <span className="text-3xl font-black text-white counter-up">100%</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-60">Sécurisé</span>
            </div>
            <div className="h-10 w-px bg-slate-800" />
            <div className="flex flex-col">
              <span className="text-3xl font-black text-white leading-none">24/7</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-60">Monitoring</span>
            </div>
          </div>
        </div>
      </section>

      <section className="flex-1 flex flex-col bg-white relative overflow-y-auto">
        {/* Top Header Navigation - Mobile/Desktop Responsive */}
        <div className="w-full p-6 lg:p-0 lg:absolute lg:top-10 lg:right-12 flex justify-between items-center lg:flex-row-reverse z-20">
          <Link to="/" className="lg:hidden transition-transform active:scale-95">
            <Logo className="text-3xl" color="#000000" />
          </Link>
          <a 
            href="https://domotus.ma" 
            className="flex items-center gap-2 lg:gap-3 text-[9px] lg:text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-blue-600 transition-all group px-3 py-2.5 lg:px-4 bg-white border border-slate-200 lg:bg-slate-50/50 lg:backdrop-blur-sm lg:border-slate-100 rounded-xl hover:shadow-lg shadow-sm lg:shadow-none"
          >
            <ArrowLeft className="w-3.5 h-3.5 lg:w-4 lg:h-4 transition-transform group-hover:-translate-x-1" /> <span className="hidden sm:inline">RETOUR VERS LE SITE WEB</span><span className="sm:hidden">RETOUR</span>
          </a>
        </div>

        <div className="flex-1 flex flex-col justify-center lg:justify-start max-w-md w-full mx-auto px-6 lg:px-0 mt-4 lg:mt-0 pb-12" style={{ animationDelay: '0.1s' }}>
          <header className="mb-8 lg:mb-12 lg:pt-40 flex flex-col items-center lg:items-start text-center lg:text-left">
            <div className="flex flex-col items-center lg:items-start gap-3 mb-6 lg:mb-8">
              <Link to="/" className="hidden lg:block">
                <h1 className="sr-only">Domotus - Connexion</h1>
                <Logo className="text-6xl -mt-2" color="#000000" />
              </Link>
              <div className="inline-flex items-center gap-2.5 text-[10px] lg:text-[11px] font-black uppercase tracking-[0.4em] lg:tracking-[0.5em] text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-600/10 lg:bg-transparent lg:border-none lg:p-0">
                <Lock className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Accès Sécurisé
              </div>
            </div>
            <p className="text-slate-600 text-xs lg:text-sm font-medium leading-relaxed lg:pl-1.5 max-w-[300px] lg:max-w-[340px]">
              Veuillez vous authentifier pour accéder à votre espace de gestion.
            </p>
          </header>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="relative group">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-blue-600 transition-colors" />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="EMAIL" 
                  className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-5 pl-14 pr-14 text-[13px] font-bold text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:bg-white transition-all ring-offset-0 placeholder:uppercase"
                  required 
                />
              </div>
              <div className="relative group">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-blue-600 transition-colors" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="MOT DE PASSE" 
                  className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-5 pl-14 pr-14 text-[13px] font-bold text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:bg-white transition-all ring-offset-0 placeholder:uppercase"
                  required 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-blue-600 transition-colors z-10"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end px-4">
                <Link to="#" className="text-[11px] font-bold text-blue-600 hover:text-slate-950 transition-colors uppercase tracking-[0.2em]">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            <button 
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="w-full bg-blue-600 text-white py-5 rounded-2xl text-[13px] font-black uppercase tracking-[0.3em] hover:bg-slate-950 transition-all shadow-xl shadow-blue-600/20 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              ) : (
                <>SE CONNECTER <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="my-10 flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-100" />
            <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">OU</span>
            <div className="h-px flex-1 bg-slate-100" />
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={isLoading || isGoogleLoading}
            className="w-full flex justify-center items-center gap-3 py-5 px-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all duration-300 disabled:opacity-50 hover:bg-slate-50 hover:border-slate-300 active:scale-[0.98] animate-fade-up text-slate-950 shadow-sm"
            style={{ animationDelay: '0.2s' }}
          >
            {isGoogleLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-950" />
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <span>Continuer avec Google</span>
              </>
            )}
          </button>

          {/* Note de sécurité */}
          <div className="mt-10 flex items-start gap-3 p-5 bg-slate-50 rounded-2xl border border-slate-100">
            <Server className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <p className="text-[10px] font-medium text-slate-600 leading-relaxed">
              Vos données sont chiffrées de bout en bout et hébergées localement. Domotus n'exploite aucune donnée personnelle.
            </p>
          </div>
        </div>

        {/* Footer form */}
        <footer className="mt-auto pt-10 text-center animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">
            © {new Date().getFullYear()} Domotus
          </p>
        </footer>
      </section>

      {/* Decorative Blob */}
      <div className="fixed -bottom-24 -left-24 w-96 h-96 bg-blue-600/5 blur-[120px] rounded-full pointer-events-none" aria-hidden="true" />
    </main>
  );
}
